/**
 * CommandPalette — ⌘K / Ctrl+K for FDR Command (Linear pattern).
 * Fuzzy-jump to any tab or any vendor. Mounted inside the FDR module shell.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

export default function CommandPalette({ tabs, onNavigate, onOpenEntity }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [entities, setEntities] = useState([]);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o); setQ(''); setSel(0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 20);
      if (!entities.length) {
        fetch(`${API}/api/fdr/entities`, { credentials: 'include' }).then(r => r.json())
          .then(d => { if (d.success) setEntities(d.entities || []); }).catch(() => {});
      }
    }
  }, [open, entities.length]);

  const needle = q.toLowerCase().trim();
  const tabHits = tabs.filter(t => !needle || t.label.toLowerCase().includes(needle))
    .map(t => ({ type: 'tab', id: t.id, label: t.label, hint: 'Go to tab' }));
  const entHits = entities.filter(e => needle && e.name.toLowerCase().includes(needle))
    .slice(0, 8)
    .map(e => ({ type: 'entity', id: e.id, label: e.name, hint: `${(e.entity_type || '').replace(/_/g, ' ')} · ${e.status}` }));
  const results = [...entHits, ...(needle ? tabHits : tabHits.slice(0, 10))].slice(0, 12);

  const go = useCallback((r) => {
    setOpen(false);
    if (r.type === 'tab') onNavigate?.(r.id);
    else onOpenEntity?.(r.id);
  }, [onNavigate, onOpenEntity]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] bg-slate-900/30 backdrop-blur-[2px]"
      onClick={() => setOpen(false)}>
      <div className="w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200/60 overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 px-4 border-b border-slate-100">
          <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input ref={inputRef} value={q}
            onChange={e => { setQ(e.target.value); setSel(0); }}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, results.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
              if (e.key === 'Enter' && results[sel]) go(results[sel]);
            }}
            placeholder="Jump to a vendor or tab…"
            className="flex-1 py-3.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none" />
          <kbd className="text-[10px] text-slate-300 border border-slate-200 rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto py-1.5">
          {results.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-300">No matches.</div>}
          {results.map((r, i) => (
            <button key={`${r.type}:${r.id}`} onClick={() => go(r)} onMouseEnter={() => setSel(i)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${i === sel ? 'bg-orange-50' : ''}`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${r.type === 'entity' ? 'bg-orange-500' : 'bg-slate-300'}`} />
              <span className="flex-1 text-sm text-slate-700 truncate">{r.label}</span>
              <span className="text-[10px] text-slate-400 capitalize flex-shrink-0">{r.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
